import { useState, useEffect, useMemo } from "react";
import { api } from "../api/api";

const HOF = { lat: 52.4089, lon: 10.8601 };

function sprich(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "de-DE";
  u.rate = 0.95;
  window.speechSynthesis.speak(u);
}

function bautAnsage(stopp, index) {
  let text = `Stopp ${index}: ${stopp.nachname}, ${stopp.strasse} ${stopp.hausnummer}. `;
  stopp.produkte.forEach(p => {
    text += `${p.menge} ${p.einheit} ${p.produktName} liefern. `;
  });
  if (stopp.flaschenBeimKunden > 0)
    text += `${stopp.flaschenBeimKunden} Flasche${stopp.flaschenBeimKunden !== 1 ? "n" : ""} abholen. `;
  const notiz = stopp.produkte.find(p => p.notiz)?.notiz;
  if (notiz) text += `Notiz: ${notiz}.`;
  return text;
}

export default function FahrerKarte({ lieferungen, datum, tourFarbe = "#e65100" }) {
  const [geliefert,      setGeliefert]      = useState({});
  const [leergutEingabe, setLeergutEingabe] = useState({});
  const [leergutFertig,  setLeergutFertig]  = useState({});
  const [aktiverStopp,   setAktiverStopp]   = useState(0);
  const [loadingId,      setLoadingId]      = useState(null);

  // Group by userId — preserves tour order (first occurrence per user)
  const stopps = useMemo(() => {
    const map = new Map();
    lieferungen.forEach(l => {
      if (!map.has(l.userId)) {
        map.set(l.userId, {
          userId:             l.userId,
          vorname:            l.vorname,
          nachname:           l.nachname,
          strasse:            l.strasse,
          hausnummer:         l.hausnummer,
          plz:                l.plz,
          ort:                l.ort,
          telefon:            l.telefon,
          flaschenBeimKunden: l.flaschenBeimKunden,
          produkte:           [],
        });
      }
      map.get(l.userId).produkte.push({
        bestellungId: l.bestellungId,
        produktName:  l.produktName,
        menge:        l.menge,
        einheit:      l.einheit,
        istMehrweg:   l.istMehrweg,
        notiz:        l.notiz,
      });
    });
    return Array.from(map.values());
  }, [lieferungen]);

  // Init delivery + leergut state from DB (keyed by bestellungId / userId)
  useEffect(() => {
    if (!lieferungen.length) return;
    const gel = {}, eingabe = {}, fertig = {};
    lieferungen.forEach(l => {
      if (l.istGeliefert) {
        gel[l.bestellungId] = { istMehrweg: l.istMehrweg, flaschenNachLieferung: l.flaschenBeimKunden };
      }
      // Leergut keyed by userId — only init once per user (first Mehrweg product)
      if (l.istMehrweg && l.istGeliefert && !(l.userId in fertig) && !(l.userId in eingabe)) {
        if (l.leergutAbgeholt === true) {
          fertig[l.userId] = true;
        } else {
          eingabe[l.userId] = String(l.flaschenBeimKunden ?? 0);
        }
      }
    });
    setGeliefert(prev => ({ ...prev, ...gel }));
    setLeergutEingabe(prev => ({ ...prev, ...eingabe }));
    setLeergutFertig(prev => ({ ...prev, ...fertig }));
  }, [lieferungen]);

  const handleAlleGeliefert = async (stopp) => {
    const nichtGeliefert = stopp.produkte.filter(p => !geliefert[p.bestellungId]);
    if (!nichtGeliefert.length) return;
    setLoadingId(`stopp-${stopp.userId}`);
    try {
      const datumStr = datum instanceof Date
        ? datum.toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      let latestMehrwegRes = null;
      for (const p of nichtGeliefert) {
        const res = await api.fahrer.geliefert(p.bestellungId, datumStr);
        setGeliefert(prev => ({ ...prev, [p.bestellungId]: res }));
        if (res.istMehrweg) latestMehrwegRes = res;
      }
      if (latestMehrwegRes) {
        setLeergutEingabe(prev => ({
          ...prev,
          [stopp.userId]: String(latestMehrwegRes.flaschenNachLieferung),
        }));
      }
    } finally {
      setLoadingId(null);
    }
  };

  const handleLeergut = async (stopp, abgeholt) => {
    const mehrwegProdukt = stopp.produkte.find(p => p.istMehrweg);
    if (!mehrwegProdukt) return;
    setLoadingId(`leergut-${stopp.userId}`);
    try {
      await api.fahrer.leergut(mehrwegProdukt.bestellungId, abgeholt);
      setLeergutFertig(prev => ({ ...prev, [stopp.userId]: abgeholt }));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div>
      {/* Infobar */}
      {stopps.length > 0 && (
        <div className="card" style={{ background: "#fff3e0", border: "1.5px solid #ffcc80", marginBottom: "1rem", display: "flex", gap: "1rem", padding: "0.75rem 1.25rem", flexWrap: "wrap", alignItems: "center" }}>
          <span>📦 <strong>{stopps.length} Stopps</strong></span>

          <button
            onClick={() => {
              const s = stopps[aktiverStopp];
              if (!s) return;
              sprich(bautAnsage(s, aktiverStopp + 1));
              setAktiverStopp(prev => Math.min(prev + 1, stopps.length - 1));
            }}
            style={{ background: "#1565c0", color: "white", border: "none", borderRadius: "8px", padding: "0.3rem 0.9rem", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap" }}>
            🔊 Stopp {aktiverStopp + 1} vorlesen
          </button>
          {aktiverStopp > 0 && (
            <button onClick={() => setAktiverStopp(0)}
              style={{ background: "#f5f5f5", color: "#555", border: "1px solid #ddd", borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.82rem", cursor: "pointer" }}>
              ↺ Reset
            </button>
          )}

          <a
            href={(() => {
              const origin = `${HOF.lat},${HOF.lon}`;
              const stops  = stopps.map(s => encodeURIComponent(`${s.strasse} ${s.hausnummer}, ${s.plz} ${s.ort}, Deutschland`));
              const dest   = stops[stops.length - 1];
              const waypoints = stops.slice(0, -1).join("|");
              return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${waypoints ? `&waypoints=${waypoints}` : ""}&travelmode=driving`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: "#4285f4", color: "white", padding: "0.3rem 0.8rem", borderRadius: "8px", textDecoration: "none", fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
            🗺️ In Google Maps öffnen
          </a>

          {/* Gesamtmenge pro Produkt */}
          {(() => {
            const summen = {};
            lieferungen.forEach(l => {
              const key = l.produktName ?? "Unbekannt";
              if (!summen[key]) summen[key] = { menge: 0, einheit: l.einheit };
              summen[key].menge += l.menge;
            });
            return Object.entries(summen).map(([name, { menge, einheit }]) => (
              <span key={name}>🛒 <strong>{menge} {einheit} {name}</strong> mitnehmen</span>
            ));
          })()}

          {stopps.some(s => s.flaschenBeimKunden > 0) && (
            <span>🍼 <strong>{stopps.reduce((sum, s) => sum + (s.flaschenBeimKunden ?? 0), 0)} Flaschen</strong> gesamt bei Kunden</span>
          )}
        </div>
      )}

      {/* Stoppliste */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {stopps.length === 0
          ? <div className="card" style={{ textAlign: "center", color: "#aaa" }}>Keine Lieferungen.</div>
          : <>
            <div className="card" style={{ padding: "0.6rem 0.9rem", background: "#e8f5e9", border: "1.5px solid #a5d6a7" }}>
              <strong style={{ color: "#2e7d32" }}>🏠 Start: Milchhof Gehrke</strong>
            </div>
            {stopps.map((stopp, i) => {
              const alleGeliefert  = stopp.produkte.every(p => !!geliefert[p.bestellungId]);
              const hatMehrweg     = stopp.produkte.some(p => p.istMehrweg);
              const leergutBest    = leergutFertig[stopp.userId];
              const isLoading      = loadingId === `stopp-${stopp.userId}`;
              const isLeergutLoad  = loadingId === `leergut-${stopp.userId}`;

              return (
                <div key={stopp.userId} className="card" style={{ padding: "0.6rem 0.9rem", border: `1.5px solid ${alleGeliefert ? "#a5d6a7" : "#ffe0b2"}`, background: alleGeliefert ? "#f1f8f1" : "white" }}>
                  <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
                    <div style={{ background: alleGeliefert ? "#2e7d32" : tourFarbe, color: "white", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.85rem", flexShrink: 0 }}>
                      {alleGeliefert ? "✓" : i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      {/* Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong>{stopp.vorname} {stopp.nachname}</strong>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${stopp.strasse} ${stopp.hausnummer}, ${stopp.plz} ${stopp.ort}, Deutschland`)}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", padding: "0 0.2rem", textDecoration: "none" }}
                            title="In Google Maps öffnen">📍</a>
                          <button onClick={() => sprich(bautAnsage(stopp, i + 1))}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", padding: "0 0.2rem" }}
                            title="Vorlesen">🔊</button>
                        </div>
                      </div>
                      {stopp.telefon && <span style={{ color: "#888", fontSize: "0.8rem" }}>📞 {stopp.telefon}</span>}
                      <div style={{ color: "#555", fontSize: "0.85rem" }}>{stopp.strasse} {stopp.hausnummer}, {stopp.plz} {stopp.ort}</div>

                      {/* Product list */}
                      <div style={{ marginTop: "0.3rem" }}>
                        {stopp.produkte.map(p => (
                          <div key={p.bestellungId} style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: "#e65100", fontSize: "0.85rem", marginTop: "0.15rem" }}>
                            🛒 <strong>{p.menge} {p.einheit}</strong> {p.produktName}
                            {p.istMehrweg
                              ? <span style={{ background: "#e8f5e9", color: "#2e7d32", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.75rem", fontWeight: 700 }}>Mehrweg</span>
                              : <span style={{ background: "#f5f5f5", color: "#888", borderRadius: 4, padding: "0.05rem 0.4rem", fontSize: "0.75rem" }}>Einweg</span>
                            }
                            {geliefert[p.bestellungId] && <span style={{ color: "#2e7d32", fontSize: "0.8rem" }}>✓</span>}
                            {p.notiz && <span style={{ color: "#888", fontSize: "0.78rem", fontStyle: "italic" }}>📝 {p.notiz}</span>}
                          </div>
                        ))}
                      </div>

                      {hatMehrweg && stopp.flaschenBeimKunden > 0 && !alleGeliefert && (
                        <div style={{ color: "#1565c0", fontSize: "0.85rem", marginTop: "0.25rem" }}>
                          🍼 Aktuell <strong>{stopp.flaschenBeimKunden} Flasche{stopp.flaschenBeimKunden !== 1 ? "n" : ""}</strong> beim Kunden
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ marginTop: "0.6rem" }}>
                        {!alleGeliefert ? (
                          <button disabled={isLoading} onClick={() => handleAlleGeliefert(stopp)}
                            style={{ padding: "0.35rem 1rem", fontSize: "0.85rem", background: "#e8f5e9", color: "#2e7d32", border: "1.5px solid #a5d6a7", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}>
                            {isLoading ? "…" : stopp.produkte.length > 1 ? `✓ Alle ${stopp.produkte.length} zugestellt` : "✓ Zugestellt"}
                          </button>
                        ) : hatMehrweg && leergutBest === undefined ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", background: "#e3f2fd", border: "1.5px solid #90caf9", borderRadius: "8px", padding: "0.5rem 0.75rem" }}>
                            <span style={{ fontSize: "0.85rem", color: "#1565c0", fontWeight: 600 }}>🍼 Leergut mitgenommen:</span>
                            <input
                              type="number" min="0"
                              value={leergutEingabe[stopp.userId] ?? ""}
                              onChange={e => setLeergutEingabe(prev => ({ ...prev, [stopp.userId]: e.target.value }))}
                              style={{ width: "60px", padding: "0.3rem 0.4rem", borderRadius: "6px", border: "1.5px solid #90caf9", fontSize: "0.9rem", fontWeight: 600, textAlign: "center" }}
                            />
                            <span style={{ fontSize: "0.82rem", color: "#555" }}>Flaschen</span>
                            <button disabled={isLeergutLoad}
                              onClick={() => handleLeergut(stopp, parseInt(leergutEingabe[stopp.userId]) || 0)}
                              style={{ padding: "0.3rem 0.8rem", fontSize: "0.85rem", background: "#1565c0", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}>
                              {isLeergutLoad ? "…" : "Bestätigen"}
                            </button>
                            <button disabled={isLeergutLoad}
                              onClick={() => handleLeergut(stopp, 0)}
                              style={{ padding: "0.3rem 0.6rem", fontSize: "0.82rem", background: "none", border: "1px solid #90caf9", borderRadius: "6px", cursor: "pointer", color: "#555" }}>
                              Keine abgeholt
                            </button>
                          </div>
                        ) : (
                          <div style={{ fontSize: "0.85rem", color: "#2e7d32", fontWeight: 600 }}>
                            ✓ Zugestellt
                            {hatMehrweg && leergutBest !== undefined && (
                              <span style={{ marginLeft: "0.75rem", color: "#1565c0" }}>
                                · 🍼 {typeof leergutBest === "number"
                                  ? `${leergutBest} Flasche${leergutBest !== 1 ? "n" : ""} abgeholt`
                                  : "Leergut abgeholt"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        }
      </div>
    </div>
  );
}
